import type { SceneEditor } from '@zakeke/zakeke-3d-core';

// L'IMPRONTA DELLA SCENA (utente, 2026-10-21: cambiando stanza senza avviso si perdevano i dati). Le modifiche le
// segnalano i campi (`change`, `input`) e qualche comando a mano; l'occhio di una riga, il trascinamento di un campo
// numerico, il gizmo e altri comandi non lo facevano, e la testata lasciava uscire senza chiedere. Al momento di
// uscire si confronta questa impronta con quella dell'ultimo caricamento o salvataggio: diversa, si chiede.
//
// Dentro c'è ciò che il salvataggio porta via: per ogni oggetto trasformazione, visibilità e materiale; i materiali e
// le luci come li serializza Babylon (senza il contenuto delle immagini, solo i loro riferimenti); la scena di Zakeke
// (ambiente, effetti, camere salvate, impostazioni). OGGETTI, MATERIALI E LUCI SONO QUELLI DEL PRODOTTO, dagli elenchi
// del motore che l'editor mostra: la Preview aggiunge alla scena il cielo e il suo materiale, che non si salvano.

const round = (value: number) => Math.round(value * 1e4) / 1e4;
const vector = (value?: { x: number; y: number; z: number; w?: number } | null) =>
    value ? [round(value.x), round(value.y), round(value.z), value.w === undefined ? undefined : round(value.w)] : null;

// Le immagini non entrano nell'impronta col loro contenuto: Babylon le metterebbe in base64 dentro il JSON.
const withoutTextureBuffers = <T>(read: () => T): T => {
    const texture = (globalThis as any).BABYLON?.Texture;
    const previous = texture?.SerializeBuffers;
    if (texture) texture.SerializeBuffers = false;
    try {
        return read();
    } finally {
        if (texture) texture.SerializeBuffers = previous;
    }
};

const safely = <T>(read: () => T): T | null => {
    try {
        return read();
    } catch {
        return null;
    }
};

export const sceneFingerprint = (sceneEditor: SceneEditor | null | undefined, zkScene: any): string => {
    const scene = sceneEditor?.scene;
    if (!scene || !sceneEditor) return '';
    const meshes: BABYLON.AbstractMesh[] = safely(() => sceneEditor.getSceneMeshes()) ?? [];
    const materials: BABYLON.Material[] = (safely(() => sceneEditor.getAllMaterials()) ?? []).flatMap((entry: any) =>
        Array.isArray(entry?.materials) ? entry.materials : [entry]
    );
    const lights: BABYLON.Light[] = safely(() => sceneEditor.getAllLights()) ?? [];
    return withoutTextureBuffers(() =>
        JSON.stringify({
            meshes: meshes.map((mesh) => [
                mesh.id,
                mesh.name,
                mesh.isEnabled(false),
                mesh.isVisible,
                vector(mesh.position),
                vector(mesh.rotationQuaternion ?? mesh.rotation),
                vector(mesh.scaling),
                mesh.material?.id ?? null,
                mesh.parent?.id ?? null
            ]),
            materials: materials.map((material) => safely(() => material.serialize())),
            lights: lights.map((light) => safely(() => light.serialize())),
            zakeke: safely(() => zkScene?.toJSON?.())
        })
    );
};
